import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getMe, getAppSettings } from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';

const AuthContext = createContext(null);
const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem('nepwed_token');
    localStorage.removeItem('nepwed_last_activity');
    setUser(null);
    disconnectSocket();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!user) return;
    localStorage.setItem('nepwed_last_activity', Date.now().toString());
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      logout();
    }, SESSION_TIMEOUT_MS);
  }, [user, logout]);

  // Check for stale session on mount and set up activity listeners
  useEffect(() => {
    if (!user) return;
    // Check if last activity was too long ago (e.g. tab was closed and re-opened)
    const lastActivity = parseInt(localStorage.getItem('nepwed_last_activity') || '0', 10);
    if (lastActivity && Date.now() - lastActivity > SESSION_TIMEOUT_MS) {
      logout();
      return;
    }
    resetTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => resetTimer();
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [user, resetTimer, logout]);

  useEffect(() => {
    const token = localStorage.getItem('nepwed_token');
    if (token) {
      getMe()
        .then((res) => setUser(res.data))
        .catch((err) => {
          // Only a rejected token ends the session. Offline or a server error
          // keeps it, so opening the app without signal doesn't log you out.
          const status = err.response?.status;
          if (status === 401 || status === 403 || status === 404) localStorage.removeItem('nepwed_token');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loginUser = (userData, token) => {
    localStorage.setItem('nepwed_token', token);
    localStorage.setItem('nepwed_last_activity', Date.now().toString());
    setUser(userData);
  };

  // Connect Socket.IO when user is authenticated and realtime is enabled.
  // The token is read when the settings arrive, not when the effect starts:
  // picking a dashboard stores two tokens back to back, and connecting with
  // whichever request happened to finish last could resurrect the stale one.
  useEffect(() => {
    if (!user) return;
    if (!localStorage.getItem('nepwed_token')) return;
    getAppSettings()
      .then(res => {
        const realtimeEnabled = res.data?.realtime_chat_enabled !== 'false'; // default on
        connectSocket(localStorage.getItem('nepwed_token'), realtimeEnabled);
      })
      .catch(() => {
        // Settings fetch failed – connect anyway (default on)
        connectSocket(localStorage.getItem('nepwed_token'), true);
      });
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
